export function createDynamicSkew() {
    const observedElements = new WeakSet();

    const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
            updateSkew(entry.target);
        }
    });

    function parseAngle(angleValue) {
        const value = angleValue.trim();

        if (!value) return 12.1;
        if (value.endsWith("deg")) return parseFloat(value);
        if (value.endsWith("rad")) return parseFloat(value) * (180 / Math.PI);

        return parseFloat(value);
    }

    function shouldHandle(el) {
        if (!(el instanceof Element)) return false;

        const styles = getComputedStyle(el);
        return styles.getPropertyValue("--skew-dynamic").trim() === "1";
    }

    function updateSkew(el) {
        if (!(el instanceof Element)) return;
        if (!shouldHandle(el)) return;

        const styles = getComputedStyle(el);
        const angle = parseAngle(styles.getPropertyValue("--skew-angle")) || 12.1;
        const height = el.offsetHeight;

        if (!height) return;

        const cut = Math.tan((angle * Math.PI) / 180) * height;
        el.style.setProperty("--skew-cut", `${cut}px`);
    }

    function registerElement(el) {
        if (!(el instanceof Element)) return;
        if (observedElements.has(el)) return;
        if (!shouldHandle(el)) return;

        observedElements.add(el);
        updateSkew(el);
        resizeObserver.observe(el);
    }

    function scan(root = document.documentElement) {
        if (!(root instanceof Element)) return;

        registerElement(root);

        const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT);

        let node = walker.nextNode();
        while (node) {
            registerElement(node);
            node = walker.nextNode();
        }
    }

    scan(document.documentElement);

    const mutationObserver = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
            mutation.addedNodes.forEach((node) => {
                if (node instanceof Element) {
                    scan(node);
                }
            });

            if (mutation.type === "attributes" && mutation.target instanceof Element) {
                registerElement(mutation.target);
                updateSkew(mutation.target);
            }
        }
    });

    mutationObserver.observe(document.documentElement, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["class", "style"],
    });

    window.addEventListener("load", () => {
        scan(document.documentElement);
    });

    return {
        refresh() {
            scan(document.documentElement);
        },
        destroy() {
            resizeObserver.disconnect();
            mutationObserver.disconnect();
        },
    };
}